import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDeleteNavigation, useNavigation, useUpdateNavigation } from 'data/hooks/navigation'
import {
    NavigationAppMenuItem,
    NavigationAppMenuItemFolder,
    NavigationAppMenuItemView,
} from 'features/navigation/types'

import { useToast } from 'ui/components/Toast'

const LOADING_SLOW_THRESHOLD_TIMEOUT = 2000

type UseNavigationEditorItemStateProps = {
    item: NavigationAppMenuItem
    isNested?: boolean
    homepageItemSid?: string
    editingItemId?: string
    setEditingItemId: (id?: string) => void
}

export function useNavigationEditorItemState({
    item,
    isNested,
    homepageItemSid,
    editingItemId,
    setEditingItemId,
}: UseNavigationEditorItemStateProps) {
    const isEditing = editingItemId === item.id

    const onEditClick = useCallback(() => {
        setEditingItemId(item.id)
    }, [item.id, setEditingItemId])

    const onCancelClick = useCallback(() => {
        setEditingItemId(undefined)
    }, [setEditingItemId])

    const { mutateAsync: updateNavigation } = useUpdateNavigation()
    const { data: navigation = [] } = useNavigation()

    const currentNavItem = (navigation as NavigationDto[]).find(
        (navItem) => navItem._sid === item.id
    )
    const currentNavItemRef = useRef(currentNavItem)
    currentNavItemRef.current = currentNavItem

    const [isSaving, setIsSaving] = useState(false)
    const [isSavingSlow, setIsSavingSlow] = useState(false)
    useEffect(() => {
        let timer: number

        if (isSaving) {
            timer = window.setTimeout(() => {
                setIsSavingSlow(true)
            }, LOADING_SLOW_THRESHOLD_TIMEOUT)
        } else {
            setIsSavingSlow(false)
        }

        return () => {
            window.clearTimeout(timer)
        }
    }, [isSaving])

    const toast = useToast()

    const onSubmit = useCallback(
        async (data: Record<string, unknown>) => {
            const prevItem = currentNavItemRef.current

            setIsSaving(true)
            try {
                await updateNavigation({
                    id: item.id,
                    patch: {
                        label: data.name,
                        options: {
                            ...prevItem?.options,
                            icon: data.icon,
                        },
                    } as Partial<NavigationDto>,
                })
                setEditingItemId(undefined)
            } catch {
                toast({
                    type: 'error',
                    title: 'There was a problem updating the navigation item',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            } finally {
                setIsSaving(false)
            }
        },
        [item.id, setEditingItemId, toast, updateNavigation]
    )

    const onInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Escape') {
                onCancelClick()
            }
        },
        [onCancelClick]
    )

    const showIcon = isNested ? false : item.type === 'view'

    const isHomepage = item.id === homepageItemSid

    const isFolder = item.type === 'folder'
    const isSeparator = item.type === 'separator'

    const supportsVisibilityControl = item.type === 'view' && item.children.length < 1

    const supportsDelete = isFolder || isSeparator
    const isDeleteDisabled =
        isSavingSlow || (isFolder && (item as NavigationAppMenuItemFolder).children?.length !== 0)

    const { mutateAsync: deleteNavigation } = useDeleteNavigation()
    const onDeleteClick = useCallback(async () => {
        if (isDeleteDisabled) return

        try {
            await deleteNavigation(item.navigationItemSid)
            setEditingItemId(undefined)
        } catch {
            toast({
                type: 'error',
                title: 'There was a problem deleting the navigation item',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        }
    }, [isDeleteDisabled, deleteNavigation, item.navigationItemSid, setEditingItemId, toast])

    const isWithinSeparator = (item as NavigationAppMenuItemView).withinSeparatorId && !isNested

    return useMemo(
        () => ({
            isEditing,
            onEditClick,
            onCancelClick,
            onSubmit,
            onInputKeyDown,
            isSavingSlow,
            showIcon,
            isHomepage,
            isFolder,
            isSeparator,
            supportsVisibilityControl,
            supportsDelete,
            isDeleteDisabled,
            onDeleteClick,
            isWithinSeparator,
        }),
        [
            isEditing,
            onEditClick,
            onCancelClick,
            onSubmit,
            onInputKeyDown,
            isSavingSlow,
            showIcon,
            isHomepage,
            isFolder,
            isSeparator,
            supportsVisibilityControl,
            supportsDelete,
            isDeleteDisabled,
            onDeleteClick,
            isWithinSeparator,
        ]
    )
}
